/**
 * MetaverseChat — Chat overlay panel for talking to Bitsy inside the 3D viewer
 * Triggers mood changes on the 3D avatar via onMoodChange callback
 */
import { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Sparkles, Brain, ShoppingBag, ExternalLink, Globe, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const MOOD_LABELS = {
  thinking: { icon: 'blue', text: 'Thinking...' },
  remembering: { icon: 'purple', text: 'Remembering you' },
  excited: { icon: 'amber', text: 'Got a recommendation!' },
};

function ChatBubble({ msg }) {
  const isUser = msg.role === 'user';
  const moodInfo = !isUser && msg.mood && MOOD_LABELS[msg.mood];
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`chat-msg-${msg.role}`}>
      <div className={`max-w-[85%] ${
        isUser
          ? 'bg-[#F7931A] text-black rounded-xl rounded-br-sm px-3 py-2'
          : 'rounded-xl rounded-bl-sm'
      }`}>
        {!isUser && msg.hasMemory && (
          <div className="flex items-center gap-1 px-3 pt-1.5 pb-0">
            <Brain className="h-2.5 w-2.5 text-purple-400" />
            <span className="text-[8px] text-purple-400/70">Personalized</span>
          </div>
        )}
        {!isUser && moodInfo && (
          <div className={`flex items-center gap-1 px-3 pt-1.5 pb-0`}>
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${
              msg.mood === 'remembering' ? 'bg-purple-400' : msg.mood === 'excited' ? 'bg-amber-400' : 'bg-blue-400'
            }`} />
            <span className={`text-[8px] ${
              msg.mood === 'remembering' ? 'text-purple-400/70' : msg.mood === 'excited' ? 'text-amber-400/70' : 'text-blue-400/70'
            }`}>{moodInfo.text}</span>
          </div>
        )}
        <div className={`px-3 py-2 text-xs leading-relaxed ${
          isUser ? '' : 'bg-white/5 text-gray-300 border border-white/5 rounded-xl rounded-bl-sm'
        }`}>
          {msg.content}
          {!isUser && msg.productLinks?.length > 0 && (
            <div className="mt-2 space-y-1" data-testid="product-links">
              {msg.productLinks.map((link, i) => {
                const url = typeof link === 'string' ? link : link.url;
                const name = typeof link === 'string' ? 'View Product' : link.product_name;
                const vendor = typeof link === 'string' ? '' : link.vendor_name;
                return (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-2 py-1.5 bg-[#F7931A]/10 border border-[#F7931A]/20 rounded-lg hover:bg-[#F7931A]/20 transition-colors group"
                    data-testid={`product-link-${i}`}
                  >
                    <ShoppingBag className="w-3 h-3 text-[#F7931A] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-[#F7931A] font-medium truncate">{name}</p>
                      {vendor && <p className="text-[8px] text-gray-500">{vendor}</p>}
                    </div>
                    <ExternalLink className="w-2.5 h-2.5 text-gray-600 group-hover:text-[#F7931A] shrink-0" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MetaverseChat({ hotelId, roomType, guestId, initialGreeting, onMoodChange, onClose }) {
  const defaultMsg = initialGreeting
    ? { role: 'assistant', content: initialGreeting, hasMemory: true, mood: 'remembering' }
    : { role: 'assistant', content: "Hi! I'm Bitsy, your virtual concierge. I can help you with room details, amenities, pricing, or booking. What would you like to know?" };
  const [messages, setMessages] = useState([defaultMsg]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `metaverse_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  // Pending passport-save suggestion surfaced after a Bitsy reply
  // { wallet, items: [{category, tag, source_hotel_id}] }
  const [passportPrompt, setPassportPrompt] = useState(null);
  const [passportSaving, setPassportSaving] = useState(false);
  const [passportSaved, setPassportSaved] = useState(null);
  const scrollRef = useRef(null);

  // Trigger remembering mood on mount if initial greeting exists
  useEffect(() => {
    if (initialGreeting && onMoodChange) onMoodChange('remembering');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    // Trigger thinking mood on avatar
    if (onMoodChange) onMoodChange('thinking');

    try {
      const res = await fetch(`${API}/api/metaverse/bitsy/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId,
          session_id: sessionId,
          message: text,
          guest_id: guestId || 'anonymous',
          room_type: roomType || null,
        }),
      });
      const data = await res.json();
      const replyContent = data.message || 'Sorry, I had trouble processing that.';

      // Determine response mood
      let responseMood = null;
      if (data.product_links?.length > 0) {
        responseMood = 'excited';
      } else if (data.has_guest_context && data.memories_stored > 0) {
        responseMood = 'remembering';
      } else if (replyContent.toLowerCase().includes('recommend') || replyContent.toLowerCase().includes('suggest') || replyContent.toLowerCase().includes('perfect for')) {
        responseMood = 'excited';
      } else if (data.has_guest_context) {
        responseMood = 'remembering';
      }

      // Trigger mood on avatar
      if (onMoodChange) onMoodChange(responseMood || 'excited');

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: replyContent,
        hasMemory: data.has_guest_context,
        memoriesStored: data.memories_stored || 0,
        mood: responseMood,
        productLinks: data.product_links || [],
      }]);

      // If Bitsy flagged a portable preference, surface the consent prompt.
      if (data.passport_wallet && Array.isArray(data.passport_suggestions) && data.passport_suggestions.length > 0) {
        setPassportPrompt({
          wallet: data.passport_wallet,
          items: data.passport_suggestions,
        });
        setPassportSaved(null);
      }
    } catch {
      if (onMoodChange) onMoodChange('idle');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection lost. Please try again.' }]);
    } finally {
      setSending(false);
    }
  };

  const savePassport = async () => {
    if (!passportPrompt || passportSaving) return;
    setPassportSaving(true);
    try {
      const res = await fetch(`${API}/api/passport/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet: passportPrompt.wallet,
          consent: true,
          items: passportPrompt.items,
        }),
      });
      if (!res.ok) throw new Error('save failed');
      setPassportSaved({ count: passportPrompt.items.length });
      setPassportPrompt(null);
    } catch {
      setPassportSaved({ error: true });
    } finally {
      setPassportSaving(false);
    }
  };

  const dismissPassport = () => setPassportPrompt(null);

  return (
    <div
      className="absolute top-0 right-0 bottom-0 w-80 bg-[#0a0e1a]/95 backdrop-blur-xl border-l border-white/5 flex flex-col z-30"
      data-testid="metaverse-chat"
    >
      {/* Header */}
      <div className="h-12 border-b border-white/5 flex items-center justify-between px-3 shrink-0">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
            <Sparkles className="h-3 w-3 text-white" />
          </div>
          <div>
            <p className="text-xs font-medium text-white">Bitsy</p>
            <p className="text-[9px] text-emerald-400">Online</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-white" onClick={() => { if (onMoodChange) onMoodChange('idle'); onClose(); }} data-testid="chat-close-btn">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="chat-messages">
        {messages.map((msg, i) => <ChatBubble key={i} msg={msg} />)}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white/5 border border-white/5 rounded-xl rounded-bl-sm px-3 py-2 flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" />
              <span className="text-[9px] text-blue-400/60">Thinking...</span>
            </div>
          </div>
        )}
      </div>

      {/* Passport consent card — surfaces only when Bitsy flags a portable preference */}
      {passportPrompt && (
        <div
          className="mx-3 mb-2 mt-1 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-3"
          data-testid="passport-consent-card"
        >
          <div className="flex items-start gap-2 mb-2">
            <Globe className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-amber-200 leading-snug">Save to your BHERMES Passport?</p>
              <p className="text-[10px] text-amber-200/70 leading-snug mt-0.5">
                So every hotel in the network knows — no re-asking.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 mb-2" data-testid="passport-suggestion-tags">
            {passportPrompt.items.map((it, i) => (
              <span
                key={`${it.category}-${it.tag}-${i}`}
                className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-100"
                data-testid={`passport-tag-${it.category}-${it.tag}`}
              >
                {it.category}: {it.tag.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
          <div className="flex gap-1.5">
            <Button
              size="sm"
              className="h-7 flex-1 bg-amber-500 hover:bg-amber-500/90 text-black text-[10px] font-semibold"
              onClick={savePassport}
              disabled={passportSaving}
              data-testid="passport-save-btn"
            >
              {passportSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> Save to Passport</>}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-[10px] text-gray-500 hover:text-white hover:bg-white/5"
              onClick={dismissPassport}
              disabled={passportSaving}
              data-testid="passport-dismiss-btn"
            >
              Not now
            </Button>
          </div>
        </div>
      )}

      {passportSaved && !passportPrompt && (
        <div
          className="mx-3 mb-2 mt-1 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2"
          data-testid="passport-saved-toast"
        >
          {passportSaved.error ? (
            <p className="text-[10px] text-red-300">Couldn't save — try again later.</p>
          ) : (
            <p className="text-[10px] text-emerald-200 flex items-center gap-1">
              <Check className="h-3 w-3" /> Saved {passportSaved.count} preference{passportSaved.count === 1 ? '' : 's'} to your BHERMES Passport.
            </p>
          )}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-white/5 p-2 shrink-0">
        <div className="flex gap-1.5">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Ask Bitsy anything..."
            className="flex-1 h-8 px-3 rounded-lg bg-white/5 border border-white/5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#F7931A]/30"
            disabled={sending}
            data-testid="chat-input"
          />
          <Button
            size="icon"
            className="h-8 w-8 bg-[#F7931A] hover:bg-[#F7931A]/90 text-black shrink-0"
            onClick={sendMessage}
            disabled={sending || !input.trim()}
            data-testid="chat-send-btn"
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
